import { useMemo, useState } from 'react';
import { rankLabel, rummy500LayoffSpot, rummy500MeldError, type Rank, type Rummy500Legal, type Rummy500Meld, type Rummy500View } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { toast } from '../store/ui';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const SUIT: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const rankNum = (id: string): number => '23456789TJQKA'.indexOf(id[0]!) + 2;
const faceOf = (id: string): string => `${rankLabel(rankNum(id) as Rank)}${SUIT[id[1]!] ?? ''}`;
const isRed = (id: string): boolean => id[1] === 'H' || id[1] === 'D';

/** Hand order for melding: by suit, then rank (high first), so runs sit together. */
const handSort = (cards: readonly string[]): string[] => [...cards].sort((a, b) => a.charCodeAt(1) - b.charCodeAt(1) || rankNum(b) - rankNum(a));

function MeldChip({ m, onClick, mine, name }: { m: Rummy500Meld; onClick?: () => void; mine: boolean; name: string }) {
  return (
    <button type="button" className={`pill ${mine ? 'accent' : ''}`} onClick={onClick} aria-label={`${name}: ${m.cards.map(faceOf).join(' ')}`} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      {m.cards.map((c, i) => (
        <span key={c} style={{ color: isRed(c) ? 'var(--danger)' : undefined, marginRight: i < m.cards.length - 1 ? 4 : 0 }}>
          {faceOf(c)}
        </span>
      ))}
    </button>
  );
}

export function Rummy500Table({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as Rummy500View;
  const legal = useGame((s) => s.legal) as Rummy500Legal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [selected, setSelected] = useState<string[]>([]);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && Boolean(turn?.seats.includes(mySeat));
  const hand = useMemo(() => handSort(view.hand), [view.hand]);
  const twoRows = hand.length > 12;
  const size = twoRows || narrow ? 'md' : 'lg';
  const drawing = myTurn && legal?.kind === 'draw';
  const playing = myTurn && legal?.kind === 'play';
  const takeable = new Set(drawing && legal?.kind === 'draw' ? legal.takeable : []);
  const handOver = view.phase === 'HAND_END' || view.phase === 'MATCH_END';
  const lastResult = view.handResults.at(-1);
  const mustUse = playing && legal?.kind === 'play' ? legal.mustUse : null;

  const toggle = (c: string) => setSelected((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  const done = (ok: boolean | void) => {
    if (ok !== false) setSelected([]);
  };

  const doMeld = () => {
    const err = rummy500MeldError(selected);
    if (err) {
      toast(err, 'error');
      return;
    }
    void act({ type: 'meld', cards: selected }).then(done);
  };

  const doLayoff = (m: Rummy500Meld) => {
    if (selected.length !== 1) {
      toast(t('r5.layoffHint'), 'error');
      return;
    }
    const card = selected[0]!;
    if (rummy500LayoffSpot(m, card) === null) {
      toast(t('r5.noFit'), 'error');
      return;
    }
    void act({ type: 'layoff', meldId: m.id, card }).then(done);
  };

  const cardEls = hand.map((c) => (
    <PlayingCard key={c} id={c} size={size} selected={selected.includes(c) || mustUse === c} playable={playing} onClick={() => (playing ? toggle(c) : undefined)} />
  ));
  const split = twoRows ? Math.ceil(cardEls.length / 2) : cardEls.length;

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={4}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.scores[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {!handOver && <span className="tag">{view.handCounts[s]}</span>}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('r5.hand', { n: view.round })} · {t('r5.stock', { n: view.stockCount })}
          </div>
          {handOver && lastResult ? (
            <div className="card glass" style={{ textAlign: 'center' }} role="status">
              <strong>{view.wentOut !== null ? t('r5.wentOut', { name: name(view.wentOut) }) : t('r5.stockOut')}</strong>
              <div className="col" style={{ marginTop: 6, gap: 4 }}>
                {lastResult.map((r, s) => (
                  <span key={s} className={`pill ${r.total > 0 ? 'accent' : ''}`}>
                    {s === mySeat ? t('r5.you') : name(s)}: {r.total > 0 ? '+' : ''}{r.total}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="col" style={{ alignItems: 'center', gap: 4, width: '100%' }}>
                <div className="row" style={{ gap: 10, alignItems: 'flex-end', justifyContent: 'center', maxWidth: '100%' }}>
                  <PlayingCard faceDown size="sm" />
                  <div role="group" aria-label={t('r5.pile', { n: view.pile.length })} style={{ display: 'flex', gap: 2, overflowX: 'auto', maxWidth: '78%', padding: '4px 2px' }}>
                    {view.pile.map((c) => (
                      <PlayingCard key={c} id={c} size="sm" playable={drawing ? takeable.has(c) : undefined} onClick={drawing && takeable.has(c) ? () => void act({ type: 'takePile', card: c }).then(done) : undefined} />
                    ))}
                  </div>
                </div>
                <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{drawing ? t('r5.tapPile') : t('r5.pile', { n: view.pile.length })}</span>
              </div>
              <div className="row wrap" style={{ justifyContent: 'center', gap: 4, maxHeight: 96, overflowY: 'auto' }} aria-label={t('r5.melds')}>
                {view.melds.length === 0 ? (
                  <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('r5.noMelds')}</span>
                ) : (
                  view.melds.map((m) => <MeldChip key={m.id} m={m} mine={m.by.includes(mySeat ?? -1)} name={t('r5.meld')} onClick={playing ? () => doLayoff(m) : undefined} />)
                )}
              </div>
              {mustUse && <span className="center-note" style={{ fontSize: 'var(--fs-xs)' }}>{t('r5.mustUse', { card: faceOf(mustUse) })}</span>}
            </>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{view.scores[mySeat] ?? 0} pts</> : null} />
      </div>
      <div className="table-bottom">
        <div role="group" aria-label="Your hand" className="hand-rows">
          <Hand size={size} label={twoRows ? 'Your hand, row 1' : 'Your hand'} maxStep={0.6}>
            {cardEls.slice(0, split)}
          </Hand>
          {twoRows && (
            <Hand size={size} label="Your hand, row 2" maxStep={0.6}>
              {cardEls.slice(split)}
            </Hand>
          )}
        </div>
        <div className="action-bar">
          {drawing && (
            <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'draw' }).then(done)}>
              {t('r5.draw')}
            </button>
          )}
          {playing && (
            <>
              <button className="btn primary" disabled={acting || selected.length < 3} onClick={doMeld}>
                {t('r5.meldBtn')}
              </button>
              <button className="btn gold" disabled={acting || selected.length !== 1 || Boolean(mustUse)} onClick={() => selected[0] && void act({ type: 'discard', card: selected[0] }).then(done)}>
                {t('r5.discard')}
              </button>
            </>
          )}
          {myTurn && selected.length > 0 && (
            <button className="btn ghost sm" onClick={() => setSelected([])}>
              {t('r5.clear')}
            </button>
          )}
        </div>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2, 3].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 80 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>H{r + 1}</td>
                {row.map((x, s) => (
                  <td key={s} className="num">
                    {x.total}
                    <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                      {x.melded}−{x.hand}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.scores.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('r5.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}
